import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ModuleRegistry } from '@ag-grid-community/core';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ImportStatusEnum } from 'exports/interfaces/import-status.enum';
import { ImportTypeEnum } from 'exports/interfaces/import-type.enum';
import { MonitoringChannelDateRangesEnum } from 'exports/interfaces/imports-monitoring-channel.interface';
import { ImportsMonitoringChannelDrilldownService } from 'exports/services/imports-monitoring-channel-drilldown.service';
import { ImportsMonitoringService } from 'exports/services/imports-monitoring.service';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DropdownFilterValue } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { ColoredIconColorsEnum } from 'shared/enums/colored-icon-colors.enum';
import { ColoredIconTypesEnum } from 'shared/enums/colored-icon-types.enum';
import { UserApiService } from 'shared/services';
import { getCachedImage } from 'shared/util/get-cached-image';

@Component({
    selector: 'app-imports-monitoring-channel-drilldown',
    imports: [TranslateModule, AgGridModule, CommonModule],
    templateUrl: './imports-monitoring-channel-drilldown.component.html',
    styleUrl: './imports-monitoring-channel-drilldown.component.scss',
})
export class ImportsMonitoringChannelDrilldownComponent extends BaseGrid implements OnInit, OnDestroy {
    protected baseUrl = environment.BASE_URL.substring(0, environment.BASE_URL.length - 1);

    logoData = signal<string | null>(null);

    public columnDefs: ColDef[] = [
        {
            headerName: 'Supplier Name',
            field: 'supplierName',
            width: 180,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            headerName: 'Listing Name',
            field: 'listingName',
            width: 250,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                links: [
                    {
                        computeUrl: (rowData: any) => `/listings/details/${rowData.listingId}/listingProfile`,
                        computeDisabledState: (rowData: any) =>
                            [ListingStatusEnum.BULK_PROCESSING].includes(rowData.listingStatus) || rowData.listingId === null,
                    },
                ],
            },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'importDate',
            valueFormatter: this.dateFormatter,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            sort: 'desc',
            sortIndex: 0,
            filterParams: {
                values: [
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastDay'),
                        value: MonitoringChannelDateRangesEnum.LAST_DAY,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastWeek'),
                        value: MonitoringChannelDateRangesEnum.LAST_WEEK,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastMonth'),
                        value: MonitoringChannelDateRangesEnum.LAST_MONTH,
                    },
                ] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
        },
        {
            headerName: 'Import Type',
            field: 'importType',
            enableValue: true,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: [
                    {
                        label: this.translateService.instant('exports.imports-monitoring.importTypes.unknown'),
                        value: ImportTypeEnum.UNKNOWN,
                    },
                    {
                        label: this.translateService.instant('exports.imports-monitoring.importTypes.createNew'),
                        value: ImportTypeEnum.CREATE_NEW_LISTING,
                    },
                    {
                        label: this.translateService.instant('exports.imports-monitoring.importTypes.overwrite'),
                        value: ImportTypeEnum.OVERWRITE_LISTING,
                    },
                ] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            cellRenderer: 'coloredIconCellRenderer',
            cellRendererParams: {
                iconType: (value: ImportTypeEnum) => {
                    switch (value) {
                        case ImportTypeEnum.CREATE_NEW_LISTING:
                            return ColoredIconTypesEnum.DIAMOND;
                        case ImportTypeEnum.OVERWRITE_LISTING:
                            return ColoredIconTypesEnum.REFRESH;
                        default:
                            return ColoredIconTypesEnum.UNKNOWN;
                    }
                },
                iconColor: (value: ImportTypeEnum) => {
                    switch (value) {
                        case ImportTypeEnum.CREATE_NEW_LISTING:
                            return ColoredIconColorsEnum.INFO;
                        case ImportTypeEnum.OVERWRITE_LISTING:
                            return ColoredIconColorsEnum.SUCCESS;
                        default:
                            return ColoredIconColorsEnum.NEUTRAL;
                    }
                },
                tooltipText: (value: ImportTypeEnum) => {
                    switch (value) {
                        case ImportTypeEnum.CREATE_NEW_LISTING:
                            return 'exports.imports-monitoring.importTypes.createNew';
                        case ImportTypeEnum.OVERWRITE_LISTING:
                            return 'exports.imports-monitoring.importTypes.overwrite';
                        default:
                            return 'exports.imports-monitoring.importTypes.unknown';
                    }
                },
            },
        },
        {
            headerName: 'Status',
            field: 'importStatus',
            cellRenderer: 'importPartialSuccessStatusDisplayRenderer',
            enableValue: true,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: [
                    {
                        label: this.translateService.instant('exports.imports-monitoring.statuses.success'),
                        value: ImportStatusEnum.SUCCESSFUL,
                    },
                    {
                        label: this.translateService.instant('exports.imports-monitoring.statuses.failure'),
                        value: ImportStatusEnum.FAILED,
                    },
                    {
                        label: this.translateService.instant('exports.imports-monitoring.statuses.partialSuccess'),
                        value: ImportStatusEnum.PARTIALLY_SUCCESSFUL,
                    },
                ] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
        },
        {
            field: '',
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 70,
            maxWidth: 70,
            headerComponent: 'resetFiltersHeader',
        },
    ];

    constructor(
        public importsMonitoringChannelDrilldownService: ImportsMonitoringChannelDrilldownService,
        public importsMonitoringService: ImportsMonitoringService,
        private translateService: TranslateService,
        private userApiService: UserApiService,
        activatedRoute: ActivatedRoute,
        router: Router,
        destroyRef: DestroyRef,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(importsMonitoringChannelDrilldownService, router, activatedRoute, destroyRef);
        effect(() => {
            const singularSourceData = this.importsMonitoringService.singularSourceData();
            if (singularSourceData) {
                const logoUrl = singularSourceData.channelLogoUrl;
                if (logoUrl) {
                    this.setLogoUrl(logoUrl);
                }
            }
        });
    }

    ngOnInit(): void {
        this.activatedRoute.params.subscribe((params) => {
            const sourceId = params['sourceId'];
            if (sourceId) {
                this.importsMonitoringChannelDrilldownService.sourceId.set(sourceId);
                this.importsMonitoringService.sourceId.set(sourceId);
            }
        });
    }

    setLogoUrl(url: string): void {
        getCachedImage(this.baseUrl + url).then((data) => {
            this.logoData.set(data);
        });
    }

    goBack(): void {
        this.router.navigate(['..'], { relativeTo: this.activatedRoute });
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.importsMonitoringChannelDrilldownService.sourceId.set(null);
        this.importsMonitoringService.sourceId.set(null);
    }
}
